import { Suspense, lazy } from "react";
import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import { SWRConfig } from "swr";
import { fetcher } from "@/lib/api";
import Shell from "@/components/Shell";
import AuthGate from "@/components/AuthGate";
import PageGuard from "@/components/PageGuard";
import DevGuard from "@/components/DevGuard";
import UsageTracker from "@/components/UsageTracker";
import ErrorBoundary from "@/components/ErrorBoundary";
import { ConfirmProvider } from "@/hooks/useConfirm";
import { ToastProvider } from "@/hooks/useToast";
import TabbedPage, {
  ANALYSIS_TABS,
  MONITORING_TABS,
  SETTINGS_TABS,
  SITES_TABS,
} from "@/components/PageTabs";
// Страницы — лениво: иначе весь дашборд (recharts и пр.) едет одним бандлом
// >1МБ. Suspense-фолбэк ниже уже был готов, но не работал при статических
// импортах. Login не лениво — это первый экран холодного захода.
import Login from "@/pages/Login";
const Dashboard = lazy(() => import("@/pages/Dashboard"));
const SitesTable = lazy(() => import("@/pages/SitesTable"));
const Health = lazy(() => import("@/pages/Health"));
const Verdicts = lazy(() => import("@/pages/Verdicts"));

// /verdicts/<хвост> → /analysis/verdicts/<хвост> (вкладка или история по дате).
function VerdictsRedirect() {
  const { pathname, search } = useLocation();
  return (
    <Navigate
      to={pathname.replace(/^\/verdicts/, "/analysis/verdicts") + search}
      replace
    />
  );
}
const Analysis = lazy(() => import("@/pages/Analysis"));
const Events = lazy(() => import("@/pages/Events"));
const Compare = lazy(() => import("@/pages/Compare"));
const Markers = lazy(() => import("@/pages/Markers"));
const Sweep = lazy(() => import("@/pages/Sweep"));
const Changes = lazy(() => import("@/pages/changes/Changes"));
const SiteDetail = lazy(() => import("@/pages/SiteDetail"));
const PageDetail = lazy(() => import("@/pages/PageDetail"));
const Settings = lazy(() => import("@/pages/Settings"));
const Backups = lazy(() => import("@/pages/Backups"));
const Regs = lazy(() => import("@/pages/Regs"));
const Reviews = lazy(() => import("@/pages/Reviews"));
const Networks = lazy(() => import("@/pages/Networks"));
const NetworkDetail = lazy(() => import("@/pages/NetworkDetail"));
const Users = lazy(() => import("@/pages/Users"));
const Logs = lazy(() => import("@/pages/Logs"));
const ServerMetrics = lazy(() => import("@/pages/ServerMetrics"));
const Crawling = lazy(() => import("@/pages/Crawling"));
const Tracking = lazy(() => import("@/pages/Tracking"));
const TrackingDetail = lazy(() => import("@/pages/TrackingDetail"));
const Finance = lazy(() => import("@/pages/Finance"));
const Recycling = lazy(() => import("./pages/Recycling"));
const Offers = lazy(() => import("@/pages/Offers"));
const Mailings = lazy(() => import("@/pages/Mailings"));
const Access = lazy(() => import("@/pages/Access"));
const Ads = lazy(() => import("@/pages/Ads"));
const AiVisibility = lazy(() => import("@/pages/AiVisibility"));
const LandMetrics = lazy(() => import("@/pages/LandMetrics"));
const Renewals = lazy(() => import("@/pages/Renewals"));
const DevUsage = lazy(() => import("@/pages/DevUsage"));
const Wiki = lazy(() => import("@/pages/Wiki"));

// Ошибка страницы не должна ронять всё приложение; при переходе на другой
// путь предохранитель сбрасывается сам.
function RouteErrorBoundary({ children }: { children: React.ReactNode }) {
  const { pathname } = useLocation();
  return <ErrorBoundary resetKey={pathname}>{children}</ErrorBoundary>;
}

export default function App() {
  return (
    <SWRConfig
      value={{
        fetcher,
        // Данные «живые»: страницы актуализируются сами, без перезагрузки.
        // Схлопываем повторные обращения к одному ключу в окне 5с.
        dedupingInterval: 5_000,
        focusThrottleInterval: 10_000,
        // Ревалидируем при возврате на вкладку / восстановлении сети / при
        // открытии страницы, если данные устарели — чтобы не жать F5.
        revalidateOnFocus: true,
        revalidateOnReconnect: true,
        revalidateIfStale: true,
        // Фоновый авто-рефреш каждые 15с — данные «живые» на всех страницах
        // (синхронизации, статистика, регистрации и т.д.).
        refreshInterval: 15_000,
        // Поллим ДАЖЕ когда вкладка считается скрытой: в части окружений
        // (фоновая вкладка, отдельное окно) document.visibilityState="hidden"
        // даже при активном просмотре — иначе автообновление молча замирает.
        refreshWhenHidden: true,
        // Плавная замена данных без мигания скелетоном при ре-фетче.
        keepPreviousData: true,
        // Не ретраить на 4xx; быстро отдаём ошибку, чтобы не било в эндпоинт.
        errorRetryCount: 1,
        errorRetryInterval: 60_000,
      }}
    >
      {/* Тосты и подтверждения — общие для всех страниц (useToast/useConfirm). */}
      <ToastProvider>
        <ConfirmProvider>
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route
              path="/*"
              element={
                <AuthGate>
                  <Shell>
                    <UsageTracker />
                    <RouteErrorBoundary>
                      <Suspense
                        fallback={
                          <div className="py-12 text-center text-sm text-ink-400">
                            Загрузка…
                          </div>
                        }
                      >
                        <Routes>
                          <Route
                            path="/"
                            element={
                              <PageGuard page="dashboard">
                                <Dashboard />
                              </PageGuard>
                            }
                          />
                          <Route
                            path="/dashboard"
                            element={<Navigate to="/" replace />}
                          />
                          <Route
                            path="/reviews"
                            element={
                              <PageGuard page="reviews">
                                <Reviews />
                              </PageGuard>
                            }
                          />
                          <Route
                            path="/regs"
                            element={
                              <PageGuard page="regs">
                                <Regs />
                              </PageGuard>
                            }
                          />
                          <Route
                            path="/regs/:tab"
                            element={
                              <PageGuard page="regs">
                                <Regs />
                              </PageGuard>
                            }
                          />
                          <Route
                            path="/recycling"
                            element={
                              <PageGuard page="recycling">
                                <Recycling />
                              </PageGuard>
                            }
                          />
                          <Route
                            path="/recycling/:tab"
                            element={
                              <PageGuard page="recycling">
                                <Recycling />
                              </PageGuard>
                            }
                          />
                          <Route
                            path="/offers"
                            element={
                              <PageGuard page="offers">
                                <Offers />
                              </PageGuard>
                            }
                          />
                          <Route
                            path="/offers/:tab"
                            element={
                              <PageGuard page="offers">
                                <Offers />
                              </PageGuard>
                            }
                          />
                          <Route
                            path="/domains"
                            element={
                              <PageGuard page="domains">
                                <Offers initialTab="domains" />
                              </PageGuard>
                            }
                          />
                          <Route
                            path="/mailings"
                            element={
                              <PageGuard page="mailings">
                                <Mailings />
                              </PageGuard>
                            }
                          />
                          <Route
                            path="/mailings/:tab"
                            element={
                              <PageGuard page="mailings">
                                <Mailings />
                              </PageGuard>
                            }
                          />
                          <Route
                            path="/finance"
                            element={
                              <PageGuard page="finance">
                                <Finance />
                              </PageGuard>
                            }
                          />
                          <Route
                            path="/finance/:tab"
                            element={
                              <PageGuard page="finance">
                                <Finance />
                              </PageGuard>
                            }
                          />
                          <Route
                            path="/networks"
                            element={
                              <PageGuard page="regs">
                                <Networks />
                              </PageGuard>
                            }
                          />
                          <Route
                            path="/networks/statuses"
                            element={
                              <PageGuard page="regs">
                                <Networks />
                              </PageGuard>
                            }
                          />
                          <Route
                            path="/networks/:id"
                            element={
                              <PageGuard page="regs">
                                <NetworkDetail />
                              </PageGuard>
                            }
                          />
                          <Route
                            path="/users"
                            element={
                              <PageGuard page="users">
                                <TabbedPage tabs={SETTINGS_TABS}>
                                  <Users />
                                </TabbedPage>
                              </PageGuard>
                            }
                          />
                          {/* Доступы — не страница роли: только разработчик (DEVELOPER_EMAILS). */}
                          <Route
                            path="/access"
                            element={
                              <DevGuard>
                                <Access />
                              </DevGuard>
                            }
                          />
                          <Route
                            path="/logs"
                            element={
                              <PageGuard page="logs">
                                <TabbedPage tabs={SETTINGS_TABS}>
                                  <Logs />
                                </TabbedPage>
                              </PageGuard>
                            }
                          />
                          <Route
                            path="/server"
                            element={
                              <PageGuard page="server">
                                <ServerMetrics />
                              </PageGuard>
                            }
                          />
                          <Route
                            path="/sites"
                            element={
                              <PageGuard page="sites">
                                <TabbedPage tabs={SITES_TABS}>
                                  <SitesTable />
                                </TabbedPage>
                              </PageGuard>
                            }
                          />
                          <Route
                            path="/health"
                            element={
                              <PageGuard page="health">
                                <TabbedPage tabs={MONITORING_TABS}>
                                  <Health />
                                </TabbedPage>
                              </PageGuard>
                            }
                          />
                          <Route
                            path="/verdicts"
                            element={
                              <Navigate to="/analysis/verdicts" replace />
                            }
                          />
                          <Route
                            path="/verdicts/*"
                            element={<VerdictsRedirect />}
                          />
                          <Route
                            path="/analysis"
                            element={
                              <PageGuard page="analysis">
                                <TabbedPage tabs={ANALYSIS_TABS}>
                                  <Analysis />
                                </TabbedPage>
                              </PageGuard>
                            }
                          />
                          <Route
                            path="/analysis/verdicts"
                            element={
                              <PageGuard page="portfolio">
                                <TabbedPage tabs={ANALYSIS_TABS}>
                                  <Verdicts />
                                </TabbedPage>
                              </PageGuard>
                            }
                          />
                          <Route
                            path="/analysis/verdicts/history/:date"
                            element={
                              <PageGuard page="portfolio">
                                <TabbedPage tabs={ANALYSIS_TABS}>
                                  <Verdicts />
                                </TabbedPage>
                              </PageGuard>
                            }
                          />
                          <Route
                            path="/analysis/verdicts/:tab"
                            element={
                              <PageGuard page="portfolio">
                                <TabbedPage tabs={ANALYSIS_TABS}>
                                  <Verdicts />
                                </TabbedPage>
                              </PageGuard>
                            }
                          />
                          <Route
                            path="/analysis/:tab"
                            element={
                              <PageGuard page="analysis">
                                <TabbedPage tabs={ANALYSIS_TABS}>
                                  <Analysis />
                                </TabbedPage>
                              </PageGuard>
                            }
                          />
                          <Route
                            path="/events"
                            element={
                              <PageGuard page="events">
                                <Events />
                              </PageGuard>
                            }
                          />
                          <Route
                            path="/crawling"
                            element={
                              <PageGuard page="crawling">
                                <TabbedPage tabs={MONITORING_TABS}>
                                  <Crawling />
                                </TabbedPage>
                              </PageGuard>
                            }
                          />
                          <Route
                            path="/indexing"
                            element={<Navigate to="/crawling" replace />}
                          />
                          <Route
                            path="/graph"
                            element={<Navigate to="/crawling" replace />}
                          />
                          <Route
                            path="/compare"
                            element={
                              <PageGuard page="compare">
                                <TabbedPage tabs={SITES_TABS}>
                                  <Compare />
                                </TabbedPage>
                              </PageGuard>
                            }
                          />
                          <Route
                            path="/markers"
                            element={
                              <PageGuard page="markers">
                                <TabbedPage tabs={SITES_TABS}>
                                  <Markers />
                                </TabbedPage>
                              </PageGuard>
                            }
                          />
                          <Route
                            path="/changes"
                            element={
                              <PageGuard page="sites">
                                <TabbedPage tabs={SITES_TABS}>
                                  <Changes />
                                </TabbedPage>
                              </PageGuard>
                            }
                          />
                          <Route
                            path="/renewals"
                            element={
                              <PageGuard page="renewals">
                                <Renewals />
                              </PageGuard>
                            }
                          />
                          <Route path="/wiki/:id?" element={<Wiki />} />
                          <Route
                            path="/tracking"
                            element={
                              <PageGuard page="tracking">
                                <TabbedPage tabs={MONITORING_TABS}>
                                  <Tracking />
                                </TabbedPage>
                              </PageGuard>
                            }
                          />
                          <Route
                            path="/tracking/:id"
                            element={
                              <PageGuard page="tracking">
                                <TrackingDetail />
                              </PageGuard>
                            }
                          />
                          <Route
                            path="/ads"
                            element={
                              <PageGuard page="ads">
                                <TabbedPage tabs={MONITORING_TABS}>
                                  <Ads />
                                </TabbedPage>
                              </PageGuard>
                            }
                          />
                          <Route
                            path="/ads/:tab"
                            element={
                              <PageGuard page="ads">
                                <TabbedPage tabs={MONITORING_TABS}>
                                  <Ads />
                                </TabbedPage>
                              </PageGuard>
                            }
                          />
                          <Route
                            path="/ai-visibility"
                            element={
                              <PageGuard page="ai-visibility">
                                <AiVisibility />
                              </PageGuard>
                            }
                          />
                          <Route
                            path="/ai-visibility/:tab"
                            element={
                              <PageGuard page="ai-visibility">
                                <AiVisibility />
                              </PageGuard>
                            }
                          />
                          <Route
                            path="/land-metrics"
                            element={
                              <PageGuard page="land-metrics">
                                <LandMetrics />
                              </PageGuard>
                            }
                          />
                          <Route
                            path="/land-metrics/:tab"
                            element={
                              <PageGuard page="land-metrics">
                                <LandMetrics />
                              </PageGuard>
                            }
                          />
                          <Route
                            path="/sweep"
                            element={
                              <PageGuard page="sweep">
                                <TabbedPage tabs={SITES_TABS}>
                                  <Sweep />
                                </TabbedPage>
                              </PageGuard>
                            }
                          />
                          <Route
                            path="/sites/table"
                            element={<Navigate to="/sites" replace />}
                          />
                          <Route
                            path="/sites/:id"
                            element={
                              <PageGuard page="sites">
                                <SiteDetail />
                              </PageGuard>
                            }
                          />
                          <Route
                            path="/sites/:id/:tab"
                            element={
                              <PageGuard page="sites">
                                <SiteDetail />
                              </PageGuard>
                            }
                          />
                          <Route
                            path="/sites/:siteId/pages/:pageId"
                            element={
                              <PageGuard page="sites">
                                <PageDetail />
                              </PageGuard>
                            }
                          />
                          <Route
                            path="/settings"
                            element={
                              <PageGuard page="settings">
                                <TabbedPage tabs={SETTINGS_TABS}>
                                  <Settings />
                                </TabbedPage>
                              </PageGuard>
                            }
                          />
                          <Route
                            path="/backups"
                            element={
                              <PageGuard page="backups">
                                <TabbedPage tabs={SETTINGS_TABS}>
                                  <Backups />
                                </TabbedPage>
                              </PageGuard>
                            }
                          />
                          {/* Статистика использования — только разработчик. */}
                          <Route
                            path="/dev/usage"
                            element={
                              <DevGuard>
                                <DevUsage />
                              </DevGuard>
                            }
                          />
                          <Route
                            path="/dev/usage/:tab"
                            element={
                              <DevGuard>
                                <DevUsage />
                              </DevGuard>
                            }
                          />
                          <Route
                            path="*"
                            element={<Navigate to="/" replace />}
                          />
                        </Routes>
                      </Suspense>
                    </RouteErrorBoundary>
                  </Shell>
                </AuthGate>
              }
            />
          </Routes>
        </ConfirmProvider>
      </ToastProvider>
    </SWRConfig>
  );
}
